import { Component } from '@builder.io/sdk';

import { lazyLoadDefaultConfig } from '../utils/constants';

export const config: Component = {
    name: 'Testimonials V2',
    inputs: [
        {
            name: 'variant',
            type: 'string',
            enum: ['1-column', '3-column'],
            required: true,
            defaultValue: '3-column',
            friendlyName: 'Layout Variant',
            helperText: 'Choose between a hero-style single testimonial or a 3-column grid layout',
        },
        {
            name: 'heading',
            type: 'string',
            required: false,
            defaultValue: '',
            friendlyName: 'Headline (Optional)',
            helperText: 'Optional headline for the testimonials section',
        },
        {
            name: 'subheading',
            type: 'richText',
            required: false,
            defaultValue: '',
            friendlyName: 'Subheading (Optional)',
            helperText: 'Optional subheading paragraph',
            onChange: (options: Map<string, string>): void => {
                const characterLimit = 300;
                const subheadingText = options.get('subheading')!;
                const htmlTagRegex = /<.*?>/gi;

                const textOnly = subheadingText.replaceAll(htmlTagRegex, '');
                if (textOnly.length <= characterLimit) {
                    return;
                }

                const tokenizedTags = [...subheadingText.matchAll(htmlTagRegex)].map((item) => item[0]);
                const tokenizedText = subheadingText.split(htmlTagRegex);

                const trimmedTextNodes = tokenizedText.reduce((textNodes, text) => {
                    const currentTextLength = textNodes.join('').length;
                    return currentTextLength + text.length <= characterLimit
                        ? textNodes.concat(text)
                        : textNodes.concat(text.slice(0, characterLimit - currentTextLength));
                }, [] as string[]);

                const trimmedRichText = tokenizedTags.map((tag, i) => trimmedTextNodes[i] + tag).join('');

                alert(`Max of ${characterLimit} characters allowed.`);
                options.set('subheading', trimmedRichText);
            },
        },
        {
            name: 'backgroundColor',
            type: 'color',
            required: false,
            defaultValue: '',
            friendlyName: 'Background Color',
            helperText: 'Choose a background color',
        },
        {
            name: 'testimonials',
            type: 'list',
            required: true,
            friendlyName: 'Testimonials',
            helperText: 'Add testimonials. Maximum of 6 testimonials allowed.',
            defaultValue: [
                {
                    name: 'Cody Wanner',
                    title: 'YouTuber & Content Creator',
                    quote: 'Whether it was necessary but hard-to-capture-myself stock for a client video, or some beautiful aerial for a YouTube video, I have used Storyblocks to create compelling, emotional, and impactful content.',
                    imageAlt: 'Photo of Cody Wanner',
                    lazyLoadImage: true,
                },
                {
                    name: 'Currently Hannah',
                    title: 'YouTuber',
                    quote: "It's so rare to find a subscription service that covers not just videos but music, sound effects, editing tools, and many more. Such a valuable tool for video production!",
                    imageAlt: 'Photo of Currently Hannah',
                    lazyLoadImage: true,
                },
                {
                    name: 'Bryan Francisco',
                    title: 'Video Creator',
                    quote: 'I love having instant access to these amazing clips to use in my video projects! Knowing I have unlimited downloads - it lets me test out different clip types to use for my projects!',
                    imageAlt: 'Photo of Bryan Francisco',
                    lazyLoadImage: true,
                },
            ],
            onChange: (options: Map<string, Array<unknown>>): void => {
                const maxAllowed = 6;
                if (options.get('testimonials')!.length > maxAllowed) {
                    options.set('testimonials', options.get('testimonials')!.slice(0, maxAllowed));
                    alert(`Maximum number of testimonials for 3-column variant is ${maxAllowed}.`);
                }
            },
            subFields: [
                {
                    name: 'name',
                    type: 'string',
                    required: true,
                    defaultValue: '[Name]',
                    friendlyName: 'Name',
                    helperText: 'The name of the person giving the testimonial',
                },
                {
                    name: 'title',
                    type: 'string',
                    required: false,
                    defaultValue: '',
                    friendlyName: 'Title/Company (Optional)',
                    helperText: 'Job title or company name',
                },
                {
                    name: 'quote',
                    type: 'longText',
                    required: true,
                    defaultValue: '[Quote]',
                    friendlyName: 'Quote Text',
                    helperText: 'The testimonial quote',
                },
                {
                    name: 'date',
                    type: 'date',
                    required: false,
                    defaultValue: '',
                    friendlyName: 'Date (Optional)',
                    helperText: 'Date of the testimonial, for structured data purposes.',
                },
                {
                    name: 'image',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
                    required: true,
                    friendlyName: 'Image',
                },
                {
                    name: 'imageAlt',
                    type: 'string',
                    required: true,
                    defaultValue: '',
                    friendlyName: 'Image Alt Text',
                    helperText: 'Alternative text for the image for accessibility',
                },
                {
                    ...lazyLoadDefaultConfig,
                },
                {
                    name: 'link',
                    type: 'url',
                    required: false,
                    defaultValue: '',
                    friendlyName: 'Link (Optional)',
                    helperText:
                        'Optional link to make the testimonial clickable. Can be internal (/path) or external (https://...)',
                },
            ],
        },
    ],
};
